import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import api from "@/axiosInstance.js";
import type { AuthContextValue, ChildrenProps, User } from "@/types/app";

const AuthContext = createContext<AuthContextValue | null>(null);
const PUBLIC_ROUTES = ["/signin", "/create-account", "/forgot-password"];
const PRIVILEGED_ROLES = ["superadmin", "superuser"];

export const AuthProvider = ({ children }: ChildrenProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const abortRef = useRef<AbortController | null>(null);
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError<{ message?: string }>(error)) {
      return error.response?.data?.message ?? error.message;
    }
    return error instanceof Error
      ? error.message
      : "An unexpected error occurred";
  };
  const getVerified = () => {
    return localStorage.getItem("verified") === "true";
  };
  const clearAuth = useCallback(() => {
    setUser(null);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("verified");
  }, []);
  const showError = useCallback((message: string) => {
    setError(message);
    toast.error(message);
  }, []);
  /*
   * Axios interceptor calls this when refresh-token fails.
   */
  useEffect(() => {
    const handleForceLogout = () => {
      clearAuth();
      navigate("/signin", {
        replace: true,
      });
      toast.error("Session expired. Please sign in again.");
    };
    window.addEventListener("forceLogout", handleForceLogout);
    return () => {
      window.removeEventListener("forceLogout", handleForceLogout);
    };
  }, [clearAuth, navigate]);
  /*
   * Verify current session and load user + permissions.
   */
  const verifyUserSession = useCallback(
    async (silent = false): Promise<User | null> => {
      if (isPublicRoute) {
        setVerifying(false);
        return null;
      }
      /*
       * Cancel previous verification request.
       */
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!silent) {
        setLoading(true);
      }
      try {
        /*
         * Verify authentication.
         * Axios interceptor will automatically attempt
         * refresh if this returns 401.
         */
        const userResponse = await api.get("/user/auth/verify-token", {
          signal: controller.signal,
        });
        const fetchedUser = userResponse?.data?.data?.user;
        if (!fetchedUser) {
          throw new Error("User is not authenticated");
        }
        /*
         * Get role and permissions.
         */
        const permissionResponse = await api.get("/user/user/permissions");
        const role = permissionResponse?.data?.data?.role || [];
        const permissions = permissionResponse?.data?.data?.permissions || [];
        const currentUser = {
          ...fetchedUser,
          role,
          permissions,
          verified: getVerified(),
        };
        setUser(currentUser);
        setError(null);
        return currentUser;
      } catch (err) {
        /*
         * Abort is expected when another verification
         * request replaces this one.
         */
        if (err?.name === "AbortError" || err?.name === "CanceledError") {
          return null;
        }
        /*
         * Axios interceptor should already have handled
         * 401 + token refresh.
         *
         * If it still reaches here, authentication failed.
         */
        if (err?.response?.status === 401) {
          clearAuth();
          if (!isPublicRoute) {
            navigate("/signin", {
              replace: true,
            });
          }
          return null;
        }
        /*
         * Don't show errors during silent background checks.
         */
        if (!silent) {
          showError(getErrorMessage(err));
        }
        return null;
      } finally {
        if (!silent) {
          setLoading(false);
        }
        setVerifying(false);
      }
    },
    [clearAuth, isPublicRoute, navigate, showError],
  );
  /*
   * Login
   */
  const handleLogin = async (data: { email: string; password: string }) => {
    if (!data?.email || !data?.password) {
      showError("Email and password are required!");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      showError("Invalid email address!");
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.post("/user/auth/login", data);
      const loggedUser = response?.data?.data?.user;
      if (!loggedUser) {
        throw new Error("Invalid login response");
      }
      /*
       * Store the basic user immediately.
       * verifyUserSession will load the complete user state.
       */
      setUser({
        ...loggedUser,
        role: [],
        permissions: [],
        verified: getVerified(),
      });
      /*
       * Load permissions and latest user data.
       */
      const currentUser = await verifyUserSession(true);
      const finalUser = currentUser || loggedUser;
      /*
       * Navigate according to onboarding state.
       */
      if (!finalUser.fc_no) {
        navigate("/add-fc", {
          replace: true,
        });
      } else if (
        PRIVILEGED_ROLES.includes(finalUser.role) &&
        !finalUser.security_key
      ) {
        navigate("/add-security-key", {
          replace: true,
        });
      } else {
        navigate("/", {
          replace: true,
        });
      }
      return true;
    } catch (err) {
      showError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  };
  /*
   * Add family code
   */
  const addFc = async (fc_no: string) => {
    if (!fc_no?.trim()) {
      showError("Family code is required!");
      return false;
    }
    setLoading(true);
    try {
      const response = await api.post("/user/auth/add-fcno", {
        fc_no: fc_no.trim(),
      });
      if (response.status === 200) {
        toast.success("Family code added successfully!");
        const currentUser = await verifyUserSession(true);
        if (currentUser) {
          navigate("/", {
            replace: true,
          });
        }
        return true;
      }
      return false;
    } catch (err) {
      showError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  };
  /*
   * Add security key
   */
  const addSecurityKey = async (securityKey: string) => {
    if (!securityKey?.trim()) {
      showError("Security key is required!");
      return false;
    }
    if (!user || !PRIVILEGED_ROLES.includes(user.role)) {
      showError("Only superadmin or superuser can add security key.");
      return false;
    }
    setLoading(true);
    try {
      const response = await api.post("/user/auth/add-seckey", {
        securityKey: securityKey.trim(),
      });
      if (response.status === 200) {
        toast.success("Security key added successfully!");
        const currentUser = await verifyUserSession(true);
        if (currentUser) {
          navigate("/", {
            replace: true,
          });
        }
        return true;
      }
      return false;
    } catch (err) {
      showError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  };
  /*
   * Verify security key
   */
  const verifySecurityKey = async (securityKey: string) => {
    if (!securityKey?.trim()) {
      showError("Security key is required!");
      return false;
    }
    if (!user || !PRIVILEGED_ROLES.includes(user.role)) {
      showError("Only superadmin or superuser can verify security key.");

      return false;
    }
    setLoading(true);
    try {
      const response = await api.post("/user/auth/validate-key", {
        securityKey: securityKey.trim(),
      });
      localStorage.setItem("verified", "true");
      setUser((currentUser) =>
        currentUser ? { ...currentUser, verified: true } : currentUser,
      );
      toast.success(
        response?.data?.message || "Security key verified successfully.",
      );

      setError(null);
      return true;
    } catch (err) {
      showError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  };
  /*
   * Logout
   */
  const handleSignOut = async () => {
    setLoading(true);
    try {
      await api.post("/user/auth/logout");
    } catch (err) {
      /*
       * Even if backend logout fails,
       * clear local authentication state.
       */
      console.error("Logout request failed:", err);
    } finally {
      clearAuth();
      setLoading(false);
      navigate("/signin", {
        replace: true,
      });
    }
  };

  /*
   * Initial authentication check.
   */
  useEffect(() => {
    verifyUserSession();
  }, [verifyUserSession]);
  /*
   * Background session verification.
   */
  useEffect(() => {
    if (isPublicRoute) {
      return;
    }
    const interval = setInterval(
      () => {
        verifyUserSession(true);
      },
      15 * 60 * 1000,
    );
    return () => {
      clearInterval(interval);
    };
  }, [isPublicRoute, verifyUserSession]);
  /*
   * Abort pending request when provider unmounts.
   */
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        verifying,
        error,
        handleLogin,
        handleSignOut,
        addFc,
        addSecurityKey,
        verifySecurityKey,

        verifyUserSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
};
